/**
 * Hierarchy view for the DMA PLG "Update products" surface (PLT-4748).
 *
 * Renders the products as an indented, expandable tree reconstructed from
 * `PiuProductState.parentId`. Read-only by default; when `dndEnabled` is set
 * (Dashboard Edit mode, behind the `blanket.plgSegmentEditor` gate) every row —
 * both leaves and segment containers — becomes an HTML5 drag source for
 * reparenting (PLT-5690): drop a row onto another (segment) row → it becomes
 * that row's child. Making a node top-level is done via the "Make top-level"
 * row action, not DnD (the top-level drop zone was removed in PLT-5799).
 * Invalid drops (self, onto a descendant → cycle, no-op, or breaching the
 * 5-level depth cap) are suppressed up-front so the browser shows the "no
 * entry" cursor; the same `assertHierarchyValid` guard the backend enforces
 * (PLT-5689) is reused here for the client-side check.
 *
 * The list view continues to be the default and is untouched by this file.
 */
import { CellContext } from '@tanstack/react-table';
import {
  assertHierarchyValid,
  formatToSubRows,
  groupHierarchically,
  WithChildren,
} from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import {
  ResolvedIpr,
  resolveIprAttributesRecursive,
} from '@upright/common/model/input/companies/products/propagate-ipr-attributes';
import { TableV2 } from '@upright/ui/common/generic/content/tables/table-v2';
import { RowExpandedToggler } from '@upright/ui/common/generic/content/tables/table-v2/utils/row-expanded-toggler';
import { ColumnV2 } from '@upright/ui/common/generic/content/tables/table-v2/types';
import { DragEvent as ReactDragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { ProductAttributesCell, ProductMatchingCell } from './modelling-attributes';
import { HierarchyRowActions } from './hierarchy-row-actions';
import { ProductEditDialog } from '../product-edit-dialog';
import { MatchingEditDialog } from '../product-edit-dialog/matching-edit-dialog';
import { ModelledAsDialog } from '../modelled-as-dialog';
import { ProvidedRevenueCell } from './provided-revenue-cell';
import { RevenueColumnHeader } from './revenue-column-header';
import { EditableProductTitleCell } from './editable-title-cell';
import { attachOwnIpr, OwnIprNode } from './ipr-inheritance';
import { computeNormalizedShares } from '../revenue-shares';

interface HierarchyProductTableProps {
  products: PiuProductState[] | undefined;
  /** Show the "Matching" column with the matched product name. Team users only. */
  showMatching?: boolean;
  /** Show the "Attributes" column with IPR attributes. Enterprise + team users. */
  showAttributes?: boolean;
  /**
   * Enable drag-and-drop reparenting of leaf products (PLT-5690). Only set on
   * the Dashboard "Update products" surface in Edit mode, behind the segment
   * editor gate. When unset the table renders read-only, byte-identical to the
   * PR 1 baseline.
   */
  dndEnabled?: boolean;
  /** Persist a reparent. Called with the dragged product id and its new parent id (`null` = top-level). */
  onReparent?: (productId: string, newParentId: string | null) => void;
  /** Open the create-product dialog with this parent pre-set (Hierarchy "Add child product", PLT-5691). */
  onAddChildProduct?: (parentId: string) => void;
  /** Create a new empty segment INSIDE this segment (Hierarchy "Add child segment", PLT-5799). */
  onAddChildSegment?: (parentId: string) => void;
  /** Insert a new segment as the PARENT of this node (Hierarchy "Add parent segment", PLT-5707). */
  onAddParentSegment?: (productId: string) => void;
  /**
   * Standard per-product row actions, kept in parity with the flat List view so
   * the Hierarchy kebab offers Edit (matching + attributes) / Duplicate / Delete
   * / Internal too. Supplied together with the DnD handlers in the gated editor.
   */
  onProductUpdate?: (product: PiuProductState) => void;
  duplicateProduct?: (product: PiuProductState) => void;
  deleteProduct?: (product: PiuProductState) => void;
  /** Folded into the row-action `disabled` state (frozen / publishing variants). */
  disabled?: boolean;
  companyId?: string;
}

// Each node in the tree also carries its own merged IPR (`ownIpr`) plus the inheritance resolved
// over the tree (`inheritedIpr` / `resolvedIpr`, PLT-5695) so `ProductAttributesCell` can show
// attributes cascading from ancestors as "(inherited)". `attachOwnIpr` / `OwnIprNode` are shared
// with the flat List view via `./ipr-inheritance`.
type HierarchyRow = WithChildren<OwnIprNode & ResolvedIpr> & { subRows: HierarchyRow[] };

export function HierarchyProductTable({
  products,
  showMatching = false,
  showAttributes = false,
  dndEnabled = false,
  onReparent,
  onAddChildProduct,
  onAddChildSegment,
  onAddParentSegment,
  onProductUpdate,
  duplicateProduct,
  deleteProduct,
  disabled = false,
  companyId,
}: HierarchyProductTableProps) {
  // Edit / Internal dialogs, mirroring the flat `ProductTable` so the Hierarchy
  // kebab reaches the same matching + attributes editor (PLT-5691).
  const [editingProduct, setEditingProduct] = useState<PiuProductState | null>(null);
  const [modelledAsProduct, setModelledAsProduct] = useState<PiuProductState | null>(null);
  // "Edit matching" opens the standalone team-only matching modal (PLT-5707).
  const [matchingProduct, setMatchingProduct] = useState<PiuProductState | null>(null);
  const handleCloseEditDialog = useCallback(() => {
    setEditingProduct(null);
  }, []);
  const handleSaveMatching = useCallback(
    (updatedProduct: PiuProductState) => {
      setMatchingProduct(null);
      onProductUpdate?.(updatedProduct);
    },
    [onProductUpdate],
  );
  const handleSaveEditedProduct = useCallback(
    (updatedProduct: PiuProductState) => {
      setEditingProduct(null);
      onProductUpdate?.(updatedProduct);
    },
    [onProductUpdate],
  );
  // Commit an inline "Provided" revenue edit (PLT-5692) via the same update path
  // as the edit dialog, which marks the set dirty (Save → Recompute).
  const handleCommitRevenue = useCallback(
    (product: PiuProductState, revenueShare: number | undefined) => {
      onProductUpdate?.({ ...product, revenueShare });
    },
    [onProductUpdate],
  );
  // Normalized shares (0..1) for every node, recomputed whenever the provided
  // values change so the Normalized column stays live during editing.
  const normalizedShares = useMemo(() => computeNormalizedShares(products ?? []), [products]);
  const hierarchicalRows = useMemo<HierarchyRow[] | undefined>(() => {
    if (!products) return undefined;
    // `groupHierarchically` tolerates undefined / null / dangling parentIds
    // (treats them as top-level), so legacy products without a `parentId`
    // field render as a flat list inside the hierarchy view.
    const grouped = groupHierarchically(products);
    // Resolve IPR attribute inheritance top-down so descendant rows can surface attributes that
    // cascade from their ancestors as "(inherited)" (PLT-5695), mirroring how blanket taxonomy
    // mappings are propagated before rendering (`propagateTaxonomyMappingsRecursive`).
    const resolved = resolveIprAttributesRecursive(attachOwnIpr(grouped));
    return resolved.map(formatToSubRows) as HierarchyRow[];
  }, [products]);

  // Ancestor-inherited attributes indexed by product id, taken from the SAME resolved tree the rows
  // render from (`hierarchicalRows`) rather than re-resolving `products` — so the edit dialog's
  // "Inherited from parent segment" hint always matches the "(inherited)" tags shown in the row, and
  // is looked up by the (stable) product id even when the row object captured by the menu is stale
  // (PLT-5799).
  const resolvedIprById = useMemo(() => {
    const byId = new Map<string, ResolvedIpr>();
    const walk = (rows: HierarchyRow[] | undefined): void => {
      for (const row of rows ?? []) {
        byId.set(row.id, { inheritedIpr: row.inheritedIpr, resolvedIpr: row.resolvedIpr });
        walk(row.subRows);
      }
    };
    walk(hierarchicalRows);
    return byId;
  }, [hierarchicalRows]);

  const productsById = useMemo(() => new Map((products ?? []).map(p => [p.id, p])), [products]);

  // Ephemeral DnD state. The dragged node's id lives in a ref, and the fade on the dragged row is
  // written straight to that row's DOM node as `data-piu-dragging` — neither is React state, and
  // that is deliberate on both counts. Deriving the fade from state made the dragged row re-render
  // mid-gesture, which Chrome answers by cancelling the drag outright, and it made un-fading the row
  // depend on `dragend` arriving. `dragend` is silently lost whenever the source node is replaced
  // mid-drag, and one dispatched on a detached node reaches no window-level listener either — so a
  // drag that ended without a successful drop (the browser fires no `drop` for a rejected target)
  // left the product name greyed out until the page was reloaded (PLT-6332).
  //
  // `draggingIdRef` also lets `onDragOver` validate targets without reading `dataTransfer`, which is
  // empty until `onDrop` in strict browsers.
  //
  // The drop-target highlight is kept the same way, for the same reason. Holding it in state was what
  // replaced the dragged row's DOM node mid-gesture: `dropTargetId` was a dependency of the `columns`
  // memo, so hovering a valid target rebuilt every column def, every cell renderer became a new
  // function identity, and React remounted the cells instead of reconciling them. That both lost the
  // source row's `dragend` and un-faded the row the moment you moved it somewhere it could go. With
  // both ids in refs, a drag now causes no re-render at all from start to finish.
  const draggingIdRef = useRef<string | null>(null);
  const dropTargetIdRef = useRef<string | null>(null);
  // Whether the fade is currently meant to be showing, i.e. we are past the one-tick delay in
  // `handleDragStart` and the drag has not ended. The row's `ref` callback consults it to decide
  // whether a freshly mounted node should be born faded.
  const fadeVisibleRef = useRef(false);

  const isValidDropTarget = useCallback(
    (targetId: string, draggedId: string): boolean => {
      if (!products) return false;
      const dragged = productsById.get(draggedId);
      if (!dragged) return false;
      // A drop target is always a row (a product id) — dropping makes the dragged node that row's
      // child. Making a node top-level is done via the "Make top-level" row action, not DnD (the
      // top-level drop zone was removed in PLT-5799).
      // Suppress no-op drops (already under this parent).
      if ((dragged.parentId ?? null) === targetId) return false;
      // PLT-5707: only business units (segments) can be parents — products are
      // always leaves. A row can be moved into a business unit, but never
      // dropped onto a product (which would make it a parent).
      const target = productsById.get(targetId);
      if (!target || target.isSegment !== true) return false;
      // Validate the tentative reparent: `assertHierarchyValid` rejects
      // self-drops and drops onto a descendant (cycles) as well as anything
      // breaching MAX_SEGMENT_DEPTH (the moved subtree included) — the same
      // guard the backend enforces on save (PLT-5689).
      const tentative = products.map(p => (p.id === draggedId ? { ...p, parentId: targetId } : p));
      try {
        assertHierarchyValid(tentative);
        return true;
      } catch {
        return false;
      }
    },
    [products, productsById],
  );

  // Strip an attribute off whatever elements currently carry it, rather than off a node remembered
  // from an earlier event: these attributes travel with the DOM, not with the React tree, so the node
  // needing cleanup is not necessarily the one last written to.
  const clearRowAttribute = useCallback((attribute: string) => {
    document.querySelectorAll(`[${attribute}]`).forEach(element => {
      element.removeAttribute(attribute);
    });
  }, []);

  const handleDragStart = useCallback(
    (event: ReactDragEvent, productId: string) => {
      event.dataTransfer.setData('text/plain', productId);
      event.dataTransfer.effectAllowed = 'move';
      // Sweep before fading this row, so a leaked fade cannot outlive the next drag even if some
      // path ever stops reaching `endDrag`.
      clearRowAttribute(DRAGGING_ATTRIBUTE);
      clearRowAttribute(DROP_TARGET_ATTRIBUTE);
      dropTargetIdRef.current = null;
      draggingIdRef.current = productId;
      // Fade on the next tick rather than here: Chrome snapshots the default drag image *after* the
      // `dragstart` handlers return, so fading the row synchronously also fades the ghost the cursor
      // carries around. Deferring a DOM write is safe where deferring a state update was not — this
      // triggers no re-render, so it cannot make Chrome cancel the drag. `event.currentTarget` is
      // null once the handler returns, hence capturing the node; and a write that lands after the
      // drag already ended is skipped by the ref check, so no cancellation handle is needed.
      fadeVisibleRef.current = false;
      const row = event.currentTarget;
      setTimeout(() => {
        if (draggingIdRef.current !== productId) return;
        fadeVisibleRef.current = true;
        row.setAttribute(DRAGGING_ATTRIBUTE, 'true');
      }, 0);
    },
    [clearRowAttribute],
  );

  const endDrag = useCallback(() => {
    draggingIdRef.current = null;
    fadeVisibleRef.current = false;
    dropTargetIdRef.current = null;
    clearRowAttribute(DRAGGING_ATTRIBUTE);
    clearRowAttribute(DROP_TARGET_ATTRIBUTE);
  }, [clearRowAttribute]);

  const handleDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>, target: string) => {
      const draggedId = draggingIdRef.current;
      if (draggedId === null) return;
      if (!isValidDropTarget(target, draggedId)) {
        // No preventDefault → the browser rejects the drop ("no entry" cursor).
        if (dropTargetIdRef.current === target) {
          dropTargetIdRef.current = null;
          event.currentTarget.removeAttribute(DROP_TARGET_ATTRIBUTE);
        }
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dropTargetIdRef.current === target) return;
      // The row being left normally clears its own highlight on `dragleave`, but a fast pointer can
      // reach the next row first; sweeping keeps at most one row highlighted whatever the order.
      clearRowAttribute(DROP_TARGET_ATTRIBUTE);
      dropTargetIdRef.current = target;
      event.currentTarget.setAttribute(DROP_TARGET_ATTRIBUTE, 'true');
    },
    [clearRowAttribute, isValidDropTarget],
  );

  const handleDragLeave = useCallback((event: ReactDragEvent<HTMLDivElement>, target: string) => {
    // `dragleave` fires when crossing a child boundary; only clear when the
    // cursor actually leaves the whole target element (relatedTarget outside).
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) return;
    if (dropTargetIdRef.current !== target) return;
    dropTargetIdRef.current = null;
    event.currentTarget.removeAttribute(DROP_TARGET_ATTRIBUTE);
  }, []);

  const handleDrop = useCallback(
    (event: ReactDragEvent, target: string) => {
      event.preventDefault();
      const draggedId = draggingIdRef.current;
      endDrag();
      if (!onReparent || draggedId === null) return;
      // The ref alone is not enough to authorise a reparent. It is cleared on every path that gives
      // us an event, but a drag whose source row is unmounted mid-gesture gives us none, so a stale
      // id can survive. Requiring the drop's own payload to match means a drag that did not start on
      // that row — a file from the desktop, text selected elsewhere on the page — cannot be mistaken
      // for it and silently move a product. `dataTransfer` is only readable here, not in `dragover`.
      if (event.dataTransfer.getData('text/plain') !== draggedId) return;
      if (!isValidDropTarget(target, draggedId)) return;
      onReparent(draggedId, target);
    },
    [endDrag, isValidDropTarget, onReparent],
  );

  // Catches drags that end where the source row's own `dragend` cannot be relied on — dropped outside
  // the window, cancelled with Esc, or ended on a node a mid-drag re-render has since replaced. Bound
  // for the table's lifetime rather than per-drag: there is no drag-scoped state left to key an
  // effect on, and an idle `endDrag` is a no-op.
  useEffect(() => {
    if (!dndEnabled) return;
    window.addEventListener('dragend', endDrag);
    window.addEventListener('drop', endDrag);
    return () => {
      window.removeEventListener('dragend', endDrag);
      window.removeEventListener('drop', endDrag);
    };
  }, [dndEnabled, endDrag]);

  const columns = useMemo<Array<ColumnV2<HierarchyRow>>>(() => {
    const baseColumns: Array<ColumnV2<HierarchyRow>> = [
      {
        id: 'title',
        header: 'Product',
        accessorKey: 'title',
        cell: (cellProps: CellContext<HierarchyRow, string>) => {
          const product = cellProps.row.original;
          // Revenue lives in the dedicated Provided / Normalized columns
          // (PLT-5692), so the title cell only carries the name + description.
          // Name + description are editable inline (double-click) in the gated
          // editor; read-only consumers get the plain text (PLT-5707).
          const inlineEditable = dndEnabled && !disabled;
          const titleContent = (
            <EditableProductTitleCell
              product={product}
              editable={inlineEditable}
              onCommit={onProductUpdate ?? (() => {})}
            />
          );
          const toggler = (
            <RowExpandedToggler
              getCanExpand={cellProps.row.getCanExpand}
              toggleExpanded={cellProps.row.toggleExpanded}
              getIsExpanded={cellProps.row.getIsExpanded}
              depth={cellProps.row.depth}
              // Only the chevron toggles expand here, so clicking a name/description
              // (e.g. to rename) doesn't collapse/expand the row (PLT-5707).
              expandOnChevronOnly
            >
              {titleContent}
            </RowExpandedToggler>
          );
          // Every row is draggable when DnD is enabled — segments and leaves are
          // both editable nodes (PLT-5690). Read-only consumers render the plain
          // toggler, byte-identical to the read-only baseline. A frozen /
          // publishing variant (`disabled`) suppresses dragging too, matching
          // the greyed-out kebab actions (PLT-5691).
          const isDraggable = dndEnabled && !disabled;
          if (!dndEnabled) {
            return toggler;
          }
          return (
            <DraggableRowContent
              // Re-assert the drag attributes on whatever node is currently standing in for this row.
              // A drag no longer re-renders anything, so during a normal gesture this changes nothing
              // — it is here because a *background* commit (a refetch, a parent re-render) still
              // remounts the cells, and a replacement node would otherwise be born without them,
              // dropping the fade halfway through the drag. React detaches and reattaches an inline
              // ref callback on every commit, so this also covers plain re-renders; re-setting an
              // attribute that is already there is a no-op.
              ref={node => {
                if (node === null) return;
                if (draggingIdRef.current === product.id && fadeVisibleRef.current) {
                  node.setAttribute(DRAGGING_ATTRIBUTE, 'true');
                }
                if (dropTargetIdRef.current === product.id) {
                  node.setAttribute(DROP_TARGET_ATTRIBUTE, 'true');
                }
              }}
              draggable={isDraggable}
              $isDraggable={isDraggable}
              onDragStart={
                isDraggable
                  ? event => {
                      handleDragStart(event, product.id);
                    }
                  : undefined
              }
              onDragEnd={endDrag}
              onDragOver={event => {
                handleDragOver(event, product.id);
              }}
              onDragLeave={event => {
                handleDragLeave(event, product.id);
              }}
              onDrop={event => {
                handleDrop(event, product.id);
              }}
            >
              {isDraggable && (
                <DragHandle aria-hidden="true" title="Drag to reparent">
                  <GrabberSvg viewBox="0 0 8 14" width="10" height="14">
                    <circle cx="2" cy="2" r="1.2" />
                    <circle cx="6" cy="2" r="1.2" />
                    <circle cx="2" cy="7" r="1.2" />
                    <circle cx="6" cy="7" r="1.2" />
                    <circle cx="2" cy="12" r="1.2" />
                    <circle cx="6" cy="12" r="1.2" />
                  </GrabberSvg>
                </DragHandle>
              )}
              {toggler}
            </DraggableRowContent>
          );
        },
        meta: {
          // Flexible so the tree fills the container width rather than leaving
          // dead space when the fixed revenue / matching columns are shown
          // (PLT-5707); floors at 24em so the name/description stay readable.
          width: 'minmax(24em, 1fr)',
        },
      },
    ];
    // Provided (raw, user-entered) + Normalized (computed share) revenue columns
    // (PLT-5692). Provided is inline-editable in the gated editor; Normalized is
    // always read-only. Both apply to leaves and segments.
    const providedEditable = dndEnabled && !disabled;
    const providedColumn: ColumnV2<HierarchyRow> = {
      id: 'provided',
      header: () => <RevenueColumnHeader />,
      cell: cellProps => (
        <ProvidedRevenueCell
          product={cellProps.row.original}
          editable={providedEditable}
          onCommit={handleCommitRevenue}
        />
      ),
      // Wide enough for the header and its info icon on one line (PLT-7027).
      meta: { width: '10.5em' },
    };
    const normalizedColumn: ColumnV2<HierarchyRow> = {
      id: 'normalized',
      header: 'Normalized weight',
      cell: cellProps => (
        <NormalizedValue>{((normalizedShares.get(cellProps.row.original.id) ?? 0) * 100).toFixed(1)}</NormalizedValue>
      ),
      meta: { width: '11em' },
    };
    // The matching / attributes cell components are shared with the flat list
    // table. `HierarchyRow` extends `PiuProductState` and the cells only read
    // fields that exist on both, so we adapt via a single cast at the boundary.
    const matchingColumn: ColumnV2<HierarchyRow> = {
      id: 'matching',
      header: 'Matching',
      accessorFn: (row: HierarchyRow) => row.productMatchResult?.bestChoice,
      cell: cellProps => ProductMatchingCell(cellProps as unknown as CellContext<PiuProductState, string>),
      meta: { width: 'auto' },
    };
    const attributesColumn: ColumnV2<HierarchyRow> = {
      id: 'attributes',
      header: 'Attributes',
      accessorFn: (row: HierarchyRow) => row.productMatchResult?.bestChoice,
      cell: cellProps => ProductAttributesCell(cellProps as unknown as CellContext<PiuProductState, string>),
      meta: { width: 'auto' },
    };
    // Per-row menu (Edit / Duplicate / Delete / Internal + the tree actions).
    // Rendered in both view and edit mode like the flat List view; the tree /
    // destructive items are gated by `disabled`, which is true in view mode.
    const actionsColumn: ColumnV2<HierarchyRow> = {
      id: 'row-actions',
      header: '',
      cell: cellProps => (
        <RowActionsCell>
          <HierarchyRowActions
            product={cellProps.row.original}
            products={products ?? []}
            onAddChildProduct={onAddChildProduct ?? (() => {})}
            onAddChildSegment={onAddChildSegment ?? (() => {})}
            onAddParentSegment={onAddParentSegment ?? (() => {})}
            onReparent={onReparent ?? (() => {})}
            onEditProduct={setEditingProduct}
            onEditMatching={setMatchingProduct}
            onDuplicateProduct={duplicateProduct ?? (() => {})}
            onDeleteProduct={deleteProduct ?? (() => {})}
            onOpenInternal={setModelledAsProduct}
            disabled={disabled}
          />
        </RowActionsCell>
      ),
      // Right-aligned with trailing whitespace so the kebab isn't flush against
      // the table's right edge in edit mode (PLT-5707), matching the flat List view.
      meta: { align: 'right', width: '4.5em' },
    };
    return [
      ...baseColumns,
      providedColumn,
      normalizedColumn,
      ...(showMatching ? [matchingColumn] : []),
      ...(showAttributes ? [attributesColumn] : []),
      // The row kebab shows in both view and edit mode, matching the flat List
      // view (PLT-5707). Edit-only items inside it stay gated by `disabled`.
      actionsColumn,
    ];
  }, [
    showMatching,
    showAttributes,
    dndEnabled,
    disabled,
    handleDragStart,
    endDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    normalizedShares,
    handleCommitRevenue,
    products,
    onReparent,
    onAddChildProduct,
    onAddChildSegment,
    onAddParentSegment,
    onProductUpdate,
    duplicateProduct,
    deleteProduct,
  ]);

  // Expand every node by default — the hierarchy is most useful showing the
  // full tree at a glance. Users can collapse interactively.
  const initialExpanded = useMemo(() => {
    if (!hierarchicalRows) return undefined;
    return getAllExpandKeys(hierarchicalRows);
  }, [hierarchicalRows]);

  if (!hierarchicalRows) {
    return <Skeleton width={'100%'} height="300px" />;
  }

  return (
    <>
      <TableV2
        columns={columns}
        data={hierarchicalRows}
        pageSize={false}
        initialState={initialExpanded ? { expanded: initialExpanded } : undefined}
        autoReset={false}
        emptyState={
          <>
            Product list is empty. <b style={{ marginLeft: '0.4em' }}>Add the first product to get started.</b>
          </>
        }
        disablePaginationControls={true}
        getRowProps={row => {
          // Leaf products (the only rows used in the computation) get a white
          // background; parent rows — business-unit segments and any product
          // that has children — get a muted grey, so the two are distinguishable
          // at a glance (PLT-5707). `getCanExpand()` is true exactly for rows
          // with children; segments are always parent-type even when empty.
          const isParentRow = row.original.isSegment === true || row.getCanExpand();
          return {
            css: css`
              height: auto;
              padding-top: var(--space__lg);
              padding-bottom: var(--space__lg);
              background-color: ${isParentRow ? 'var(--bgColor-muted, #f6f8fa)' : 'var(--bgColor-default, #ffffff)'};
            `,
          };
        }}
      />
      {editingProduct && (
        <ProductEditDialog
          mode="edit"
          onClose={handleCloseEditDialog}
          product={editingProduct}
          onSave={handleSaveEditedProduct}
          companyId={companyId}
          segmentEditorLayout
          inheritedIpr={resolvedIprById.get(editingProduct.id)?.inheritedIpr}
        />
      )}
      {matchingProduct && (
        <MatchingEditDialog
          product={matchingProduct}
          onClose={() => {
            setMatchingProduct(null);
          }}
          onSave={handleSaveMatching}
        />
      )}
      {modelledAsProduct && (
        <ModelledAsDialog
          onClose={() => {
            setModelledAsProduct(null);
          }}
          product={modelledAsProduct}
        />
      )}
    </>
  );
}

/**
 * Build the `expanded` TanStack table state that marks every parent row as
 * expanded. Keys use the TanStack row-id form (`<index>` for top-level,
 * `<parentKey>.<index>` for nested) which TableV2 also uses internally.
 */
function getAllExpandKeys(rows: HierarchyRow[]): Record<string, boolean> {
  const keys: Record<string, boolean> = {};
  function visit(row: HierarchyRow, key: string) {
    if (row.subRows.length === 0) return;
    keys[key] = true;
    row.subRows.forEach((child, index) => {
      visit(child, `${key}.${index}`);
    });
  }
  rows.forEach((row, index) => {
    visit(row, `${index}`);
  });
  return keys;
}

// Read-only computed share shown in the Normalized column (PLT-5692).
const NormalizedValue = styled.span`
  color: var(--fgColor-muted);
  white-space: nowrap;
`;

// Right-aligns the row kebab and keeps a little whitespace to the right of it so
// it isn't flush against the table edge in edit mode (PLT-5707).
const RowActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  padding-right: var(--space__sm);
`;

// Marks the row currently being dragged, so it can be faded without React state — see the DnD state
// comment in `HierarchyProductTable`. Namespaced because `clearRowAttribute` sweeps the whole
// document for them.
const DRAGGING_ATTRIBUTE = 'data-piu-dragging';
const DROP_TARGET_ATTRIBUTE = 'data-piu-drop-target';

// The title-cell wrapper that participates in HTML5 drag-and-drop. `draggable`
// is on the whole row so it can be grabbed from anywhere in the title area; the
// handle is a visual cue. `DRAGGING_ATTRIBUTE` fades the source;
// `DROP_TARGET_ATTRIBUTE` highlights the row under the cursor. Both are written
// to the DOM during the drag rather than passed as props — see the comment on
// `draggingIdRef` for why the drag deliberately re-renders nothing.
const DraggableRowContent = styled.div<{ $isDraggable: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  // Space between the drag-handle dots and the expand chevron / name (PLT-5707).
  gap: var(--space__xs);
  // Prevent text selection from hijacking the first drag gesture — without this
  // the initial mousedown-drag selects the title text instead of starting the
  // HTML5 drag, so the row only becomes draggable on a second attempt (PLT-5707).
  user-select: none;
  cursor: ${props => (props.$isDraggable ? 'grab' : 'default')};
  &:active {
    cursor: ${props => (props.$isDraggable ? 'grabbing' : 'default')};
  }
  &[${DRAGGING_ATTRIBUTE}] {
    opacity: 0.4;
  }
  &[${DROP_TARGET_ATTRIBUTE}] {
    // Blue accent highlight for the drop target, no left-edge border (PLT-5707).
    background-color: var(--bgColor-accent-muted, rgba(9, 105, 218, 0.12));
  }
  transition:
    background-color 80ms ease-out,
    box-shadow 80ms ease-out,
    opacity 120ms ease-out;
`;

// Low-contrast at rest (the row itself is the drag source); revealed on hover.
const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--fgColor-muted);
  opacity: 0;
  flex-shrink: 0;
  ${DraggableRowContent}:hover & {
    opacity: 0.8;
  }
`;

const GrabberSvg = styled.svg`
  fill: currentColor;
`;
